// Aufgaben zusammenführen (merge). Suchfeld fürs Ziel — debounced /api/search
// mit Sequenznummer gegen Race, nach dem Muster aus RecordStepList.tsx
// (Verknüpfungs-Suche). Danach eine ausdrückliche Bestätigung: der Schritt ist
// nicht umkehrbar, die Quelle wird zum Grabstein (siehe MergeCounts in
// @juuplan/shared). mergeTask/mergePersonalTask übernehmen den API-Aufruf
// samt Store-Refresh, genau wie deleteTask/deletePersonalTask.

import { useEffect, useRef, useState } from "preact/hooks";
import { ArrowDown, GitMerge, Search } from "lucide-preact";
import type { MergeCounts, PersonalTask, Task } from "@juuplan/shared";
import { Modal } from "./Modal";
import { api, ApiError } from "../lib/api";
import { toastSuccess } from "../lib/toast";
import { mergeTask } from "../store/project";
import { mergePersonalTask } from "../store/personalTasks";

import { Button } from "./ui/Button";

interface MergeSearchResult {
  type: "task" | "personal_task";
  id: number;
  title: string;
  subtitle?: string;
  project_id?: number;
}

interface Props {
  kind: "task" | "personal_task";
  source: { id: number; title: string; project_id?: number | null };
  onDone: (target: { id: number }) => void;
  onClose: () => void;
}

const KIND_LABEL: Record<Props["kind"], string> = {
  task: "Projektaufgabe",
  personal_task: "persönliche Aufgabe",
};

/**
 * Benennt nur Gattungen > 0, Singular/Plural passend — ein blosses „erledigt"
 * wäre bei einem so umfassenden Eingriff zu wenig (siehe Auftrag). „Schritte"
 * statt „Unteraufgaben" bei persönlichen Aufgaben.
 */
function describeMoved(moved: MergeCounts, kind: Props["kind"]): string {
  const subtask: [string, string] =
    kind === "task" ? ["Unteraufgabe", "Unteraufgaben"] : ["Schritt", "Schritte"];
  const rows: Array<[number, string, string]> = [
    [moved.comments, "Kommentar", "Kommentare"],
    [moved.notes, "Notiz", "Notizen"],
    [moved.attachments, "Anhang", "Anhänge"],
    [moved.checklist_items, "Checklisten-Eintrag", "Checklisten-Einträge"],
    [moved.subtasks, subtask[0], subtask[1]],
    [moved.assignees, "zuständige Person", "zuständige Personen"],
    [moved.watchers, "Beobachter", "Beobachter"],
    [moved.labels, "Label", "Labels"],
    [moved.time_entries, "Zeiteintrag", "Zeiteinträge"],
    [moved.dependencies, "Abhängigkeit", "Abhängigkeiten"],
    [moved.follow_ups, "geplante Nachfrage", "geplante Nachfragen"],
    [moved.reminders, "Erinnerung", "Erinnerungen"],
    [moved.notifications, "Benachrichtigung", "Benachrichtigungen"],
    [moved.my_day_entries, '„Mein Tag"-Eintrag', '„Mein Tag"-Einträge'],
    [moved.git_links, "Git-Verknüpfung", "Git-Verknüpfungen"],
    [moved.goal_links, "Ziel-Verknüpfung", "Ziel-Verknüpfungen"],
    [moved.inbox_items, "Posteingangs-Eintrag", "Posteingangs-Einträge"],
    [moved.references, "Referenz-Schritt", "Referenz-Schritte"],
  ];
  const parts = rows.filter(([n]) => n > 0).map(([n, sg, pl]) => `${n} ${n === 1 ? sg : pl}`);
  return parts.length > 0
    ? `Zusammengeführt: ${parts.join(", ")} übernommen.`
    : "Zusammengeführt.";
}

export function MergeTaskDialog(props: Props): preact.JSX.Element {
  const { kind, source, onDone, onClose } = props;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<MergeSearchResult[] | null>(null);
  const searchSeq = useRef(0);
  const [target, setTarget] = useState<MergeSearchResult | null>(null);
  const [busy, setBusy] = useState(false);
  // Getrennt von `results`: die Suche ist ein GET (api() toastet nur
  // Schreibfehler) und setzte bei Fehlschlag bisher `results = []` — optisch
  // nicht von "keine passende Aufgabe gefunden" zu unterscheiden. Bei einem
  // Zusammenführen-Dialog waere das besonders irreführend: der Nutzer könnte
  // annehmen, es gäbe wirklich kein Ziel, statt es erneut zu versuchen.
  const [searchFailed, setSearchFailed] = useState(false);

  // Debounced Suche, nur solange noch kein Ziel gewählt ist (danach zeigt die
  // Bestätigung — erneutes Suchen wäre verwirrend).
  useEffect(() => {
    if (target) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      setSearchFailed(false);
      return;
    }
    const seq = ++searchSeq.current;
    const t = setTimeout(() => {
      void api<{ results: MergeSearchResult[] }>(
        `/api/search?q=${encodeURIComponent(q)}&type=${kind}&status=open`
      ).then(
        (data) => {
          if (seq !== searchSeq.current) return;
          setSearchFailed(false);
          setResults(
            data.results
              .filter((r) => r.type === kind)
              .filter((r) => r.id !== source.id)
              .filter((r) => kind !== "task" || r.project_id === source.project_id)
              .slice(0, 8)
          );
        },
        () => {
          if (seq !== searchSeq.current) return;
          setSearchFailed(true);
          setResults([]);
        }
      );
    }, 250);
    return () => clearTimeout(t);
  }, [query, target, kind, source.id, source.project_id]);

  const confirm = async (): Promise<void> => {
    if (!target || busy) return;
    setBusy(true);
    try {
      let moved: MergeCounts;
      let mergedTarget: Task | PersonalTask;
      if (kind === "task") {
        const result = await mergeTask(source.id, target.id);
        moved = result.moved;
        mergedTarget = result.target;
      } else {
        const result = await mergePersonalTask(source.id, target.id);
        moved = result.moved;
        mergedTarget = result.target;
      }
      toastSuccess(describeMoved(moved, kind));
      onDone(mergedTarget);
      onClose();
    } catch (err) {
      // api() hat den Fehler (400/404/409, z.B. „bereits zusammengeführt")
      // bereits getoastet — hier nur den Ladezustand zurücksetzen.
      if (!(err instanceof ApiError)) throw err;
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Aufgaben zusammenführen" onClose={onClose} size="md">
      <div class="flex flex-col gap-3">
        {/* Richtung ZUERST und im Klartext. „Quelle"/„Ziel" allein beantwortet
            die eigentliche Frage nicht („wandert die offene Aufgabe in die
            gewaehlte oder umgekehrt?"), und der Vorgang ist nicht umkehrbar:
            ein Missverstaendnis schliesst die falsche Aufgabe. */}
        <p class="text-sm text-[color:var(--color-on-surface)]">
          <span class="font-medium">Diese Aufgabe wird geschlossen</span>, ihr Inhalt wandert in
          die Aufgabe, die du unten auswählst. Die ausgewählte Aufgabe bleibt offen.
        </p>
        <div class="flex flex-col gap-1 rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-2.5">
          <p class="text-micro uppercase tracking-wide text-[color:var(--color-on-surface-variant)]">
            Wird geschlossen (diese Aufgabe)
          </p>
          <p class="text-sm font-medium text-[color:var(--color-on-surface)]">{source.title}</p>
        </div>

        {!target && (
          <>
            <p class="text-micro uppercase tracking-wide text-[color:var(--color-on-surface-variant)]">
              Bleibt offen und übernimmt den Inhalt
            </p>
            <div class="relative">
              <Search
                size={14}
                class="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--color-on-surface-variant)]"
                aria-hidden="true"
              />
              <input
                type="text"
                autoFocus
                value={query}
                onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
                placeholder={`${KIND_LABEL[kind]} suchen, die bestehen bleibt…`}
                aria-label="Aufgabe suchen, die bestehen bleibt und den Inhalt übernimmt"
                maxLength={100}
                class="w-full rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] py-2 pl-8 pr-3 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
              />
            </div>
            {results !== null && (
              <ul class="max-h-64 overflow-y-auto rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-1">
                {searchFailed && results.length === 0 ? (
                  <li class="px-3 py-2 text-xs text-[color:var(--color-error)]">
                    Suche fehlgeschlagen — bitte erneut versuchen.
                  </li>
                ) : results.length === 0 ? (
                  <li class="px-3 py-2 text-xs italic text-[color:var(--color-on-surface-variant)]">
                    Keine passende Aufgabe gefunden
                  </li>
                ) : (
                  results.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => setTarget(r)}
                        class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus:bg-[color:var(--color-surface-container-low)]"
                      >
                        <span class="min-w-0 truncate text-[color:var(--color-on-surface)]">
                          {r.title}
                        </span>
                        {r.subtitle && (
                          <span class="ml-auto shrink-0 text-micro text-[color:var(--color-on-surface-variant)]">
                            {r.subtitle}
                          </span>
                        )}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </>
        )}

        {target && (
          <div class="flex flex-col gap-3 rounded-md border border-[color:var(--color-error)]/40 bg-[color:var(--color-error-container)]/30 p-3">
            <div class="flex flex-col gap-2 text-sm text-[color:var(--color-on-surface)]">
              <div class="flex flex-col gap-0.5">
                <span class="text-micro uppercase tracking-wide text-[color:var(--color-on-surface-variant)]">
                  Wird geschlossen
                </span>
                <span class="font-medium line-through decoration-1">„{source.title}"</span>
              </div>
              <ArrowDown
                size={14}
                class="text-[color:var(--color-on-surface-variant)]"
                aria-hidden="true"
              />
              <div class="flex flex-col gap-0.5">
                <span class="text-micro uppercase tracking-wide text-[color:var(--color-on-surface-variant)]">
                  Bleibt offen, übernimmt alles
                </span>
                <span class="font-medium">„{target.title}"</span>
              </div>
              <p>
                Kommentare, Anhänge, Erwähnungen und Benachrichtigungen ziehen mit. Das lässt sich
                nicht rückgängig machen.
              </p>
            </div>
            <div class="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setTarget(null)}
                disabled={busy}
                class="rounded-md px-3 py-1.5 text-xs text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] disabled:opacity-50"
              >
                Zurück
              </button>
              <Button
                variant="destructive"
                size="md"
                type="button"
                onClick={() => void confirm()}
                disabled={busy}
              >
                <GitMerge size={14} aria-hidden="true" />
                {busy ? "Führt zusammen…" : "Zusammenführen"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
